import { LinearGradient } from "expo-linear-gradient";
import { View, Text, StyleSheet, Pressable } from "react-native";

import { SectionRow, type SectionRowStatus } from "./SectionRow";

import type { Phase, Section, SectionStatusRecord } from "@/lib/api";
import { colors, fonts, spacing, radius, shadows, gradients } from "@/lib/theme";

interface PhaseCardProps {
    phase: Phase;
    phaseIndex: number;
    isLocked: boolean;
    isCompleted: boolean;
    sectionStatuses: SectionStatusRecord[];
    nextSectionId: string | null;
    onSectionPress: (
        sectionId: string,
        isLocked: boolean,
        lockMessage: string,
        behaviorType?: string
    ) => void;
    readOnly: boolean;
    /** If true, render as a single collapsed row instead of listing all sections */
    collapsed?: boolean;
    onCollapsedPress?: () => void;
}

function getSectionRowStatus(
    section: Section,
    sectionStatuses: SectionStatusRecord[],
    nextSectionId: string | null,
    isPhaseUnlocked: boolean,
    sectionIndex: number,
    phaseSections: Section[]
): SectionRowStatus {
    const status = sectionStatuses.find((ss) => String(ss.sectionId) === String(section.id));
    const isCompleted = status?.status === "completed";

    if (isCompleted) {
        return "completed";
    }

    if (!isPhaseUnlocked) {
        return "locked";
    }

    // Is this the "next" section?
    if (String(section.id) === String(nextSectionId)) {
        return "next";
    }

    // Check if all prior sections in this phase are completed
    const allPriorCompleted = phaseSections.slice(0, sectionIndex).every((prior) => {
        const priorStatus = sectionStatuses.find((ss) => String(ss.sectionId) === String(prior.id));
        return priorStatus?.status === "completed";
    });

    if (!allPriorCompleted) {
        return "locked";
    }

    return "not_started";
}

export function PhaseCard({
    phase,
    phaseIndex,
    isLocked,
    isCompleted,
    sectionStatuses,
    nextSectionId,
    onSectionPress,
    readOnly,
    collapsed,
    onCollapsedPress
}: PhaseCardProps) {
    const visibleSections = phase.sections.filter((s) => s.visibleOnMobile !== false);
    const completedCount = visibleSections.filter((s) => {
        const status = sectionStatuses.find((ss) => String(ss.sectionId) === String(s.id));
        return status?.status === "completed";
    }).length;

    const progress = visibleSections.length > 0 ? completedCount / visibleSections.length : 0;

    // Collapsed mode — single tappable row (used for Welcome Package)
    if (collapsed) {
        return (
            <Pressable
                onPress={() => {
                    if (isLocked) {
                        onSectionPress("", true, `Complete Phase ${phaseIndex} first`);
                    } else if (onCollapsedPress) {
                        onCollapsedPress();
                    }
                }}
                style={({ pressed }) => [
                    styles.card,
                    isLocked && styles.cardLocked,
                    pressed && styles.cardPressed
                ]}
            >
                <View style={styles.header}>
                    <View style={styles.headerLeft}>
                        <Text style={styles.phaseLabel}>PHASE {phaseIndex + 1}</Text>
                        <View style={styles.nameRow}>
                            <Text style={styles.phaseName}>{phase.displayName ?? phase.name}</Text>
                            {isCompleted ? <Text style={styles.checkIcon}>✓</Text> : null}
                            {isLocked ? <Text style={styles.lockIcon}>L</Text> : null}
                        </View>
                    </View>
                    <Text style={styles.sectionCount}>
                        {completedCount}/{visibleSections.length} sections
                    </Text>
                </View>
                <View style={styles.progressTrack}>
                    <LinearGradient
                        colors={gradients.brand}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={[styles.progressFill, { width: `${Math.round(progress * 100)}%` }]}
                    />
                </View>
            </Pressable>
        );
    }

    return (
        <View style={[styles.card, isLocked && styles.cardLocked]}>
            {/* Phase header */}
            <View style={styles.header}>
                <View style={styles.headerLeft}>
                    <Text style={styles.phaseLabel}>PHASE {phaseIndex + 1}</Text>
                    <View style={styles.nameRow}>
                        <Text style={styles.phaseName}>{phase.displayName ?? phase.name}</Text>
                        {isLocked ? <Text style={styles.lockIcon}>L</Text> : null}
                        {isCompleted ? <Text style={styles.checkIcon}>✓</Text> : null}
                    </View>
                </View>
                <Text style={styles.sectionCount}>
                    {completedCount}/{visibleSections.length} sections
                </Text>
            </View>

            {/* Progress bar */}
            <View style={styles.progressTrack}>
                <LinearGradient
                    colors={gradients.brand}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[styles.progressFill, { width: `${Math.round(progress * 100)}%` }]}
                />
            </View>

            {/* Section rows */}
            {visibleSections.map((section, i) => {
                const rowStatus = readOnly
                    ? getSectionRowStatus(
                          section,
                          sectionStatuses,
                          null,
                          true,
                          i,
                          visibleSections
                      ) === "completed"
                        ? "completed"
                        : "not_started"
                    : getSectionRowStatus(
                          section,
                          sectionStatuses,
                          nextSectionId,
                          !isLocked,
                          i,
                          visibleSections
                      );

                const sectionIsLocked = rowStatus === "locked";
                const lockMessage = isLocked
                    ? `Complete Phase ${phaseIndex} first`
                    : sectionIsLocked
                      ? `Complete the previous section first`
                      : "";

                return (
                    <SectionRow
                        key={section.id}
                        name={section.displayName ?? section.name}
                        questionCount={section.questions?.length ?? 0}
                        behaviorType={section.behaviorType}
                        status={rowStatus}
                        completedCount={
                            rowStatus === "completed" ? (section.questions?.length ?? 0) : undefined
                        }
                        onPress={() =>
                            onSectionPress(
                                String(section.id),
                                sectionIsLocked,
                                lockMessage,
                                section.behaviorType
                            )
                        }
                        isLast={i === visibleSections.length - 1}
                    />
                );
            })}
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        overflow: "hidden",
        ...shadows.sm
    },
    cardLocked: {
        opacity: 0.6
    },
    cardPressed: {
        opacity: 0.95
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: spacing.base,
        paddingBottom: spacing.sm
    },
    headerLeft: {
        flex: 1
    },
    phaseLabel: {
        fontFamily: fonts.bold,
        fontSize: 10,
        color: colors.teal500,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: 2
    },
    nameRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm
    },
    phaseName: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: colors.textPrimary
    },
    lockIcon: {
        fontFamily: fonts.bold,
        fontSize: 12,
        color: colors.gray400
    },
    checkIcon: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: colors.success500
    },
    sectionCount: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textSecondary
    },
    progressTrack: {
        height: 4,
        backgroundColor: colors.gray200,
        marginHorizontal: spacing.base,
        marginBottom: spacing.sm,
        borderRadius: 2,
        overflow: "hidden"
    },
    progressFill: {
        height: 4,
        borderRadius: 2
    }
});
